/**
 * Sette lingue in parità, dal primo giorno (14-LINGUE-E-TESTI).
 *
 * **Nessuna stringa visibile scritta a mano nel codice, nemmeno «OK».** È la regola che
 * decade per prima quando si va di fretta, ed è quella che poi costa una revisione riga
 * per riga. Aggiungerla dopo è il lavoro che non si fa mai.
 *
 * Tre scelte, e nessuna è per comodità:
 *
 *  - **I plurali passano da `Intl.PluralRules`**, non da un `if`: il polacco ha tre forme
 *    e non due (14 §5). Il turco ne ha una sola, e un `if (n === 1)` la sbaglierebbe
 *    anche lì.
 *  - **Orari e date passano da `Intl.DateTimeFormat`** con la lingua scelta. «Un `20:47`
 *    scritto a mano nel codice è un difetto in sei lingue su sette», e la guida TV è
 *    piena di orari.
 *  - **Una chiave che manca si mostra come chiave**, non ripiega sull'inglese. È brutto e
 *    onesto (14 §3): un ripiego silenzioso farebbe passare inosservata una traduzione
 *    mancante fino allo screenshot di un cliente.
 *
 * `Intl.PluralRules` e `Intl.DateTimeFormat` sono API di libreria e non le dà il `target`
 * di `tsconfig.base.json`: la loro presenza sull'annata di TV più vecchia che si vuole
 * reggere è una delle cose da verificare in fase 1, insieme al livello di compilazione.
 */

export const LOCALES = ['en', 'it', 'es', 'fr', 'de', 'pl', 'tr'] as const;
export type Locale = (typeof LOCALES)[number];
export const SOURCE_LOCALE: Locale = 'en';

export type Messages = Readonly<Record<string, string>>;
export type FormatParams = Readonly<Record<string, string | number>>;

export function isLocale(value: string): value is Locale {
  return (LOCALES as ReadonlyArray<string>).includes(value);
}

/**
 * Lingua di sistema del dispositivo; se non è fra le sette, inglese (14 §2).
 *
 * La voce manuale nelle impostazioni non è un di più: su un televisore la lingua di
 * sistema è spesso quella del paese in cui l'apparecchio è stato venduto, non quella di
 * chi lo guarda.
 */
export function resolveLocale(systemLanguages: ReadonlyArray<string>): Locale {
  for (const language of systemLanguages) {
    const base = language.split('-')[0]?.toLowerCase() ?? '';
    if (isLocale(base)) return base;
  }
  return SOURCE_LOCALE;
}

export class Translator {
  private readonly pluralRules: Intl.PluralRules;
  private readonly timeFormat: Intl.DateTimeFormat;
  private readonly dateFormat: Intl.DateTimeFormat;
  private readonly weekdayDateFormat: Intl.DateTimeFormat;
  /** Le chiavi chieste e non trovate. Il banco di misura le mostra. */
  readonly missingKeys = new Set<string>();

  constructor(
    readonly locale: Locale,
    private readonly messages: Messages,
  ) {
    this.pluralRules = new Intl.PluralRules(locale);
    this.timeFormat = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
    this.dateFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
    this.weekdayDateFormat = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }

  /**
   * Il testo per una chiave.
   *
   * Con `params.count` cerca prima la forma plurale — `key.one`, `key.few`, `key.other`,
   * secondo quello che `Intl.PluralRules` dice per questa lingua — e poi la chiave
   * secca.
   */
  t(key: string, params: FormatParams = {}): string {
    const template = this.lookup(key, params);
    if (template === null) {
      this.missingKeys.add(key);
      return key;
    }
    return template.replace(/\{(\w+)\}/g, (match, name: string) => {
      const value = params[name];
      if (value === undefined) return match;
      return typeof value === 'number' ? this.number(value) : value;
    });
  }

  private lookup(key: string, params: FormatParams): string | null {
    const count = params['count'];
    if (typeof count === 'number') {
      const category = this.pluralRules.select(count);
      const plural = this.messages[`${key}.${category}`];
      if (plural !== undefined) return plural;
      const other = this.messages[`${key}.other`];
      if (other !== undefined) return other;
    }
    return this.messages[key] ?? null;
  }

  number(value: number): string {
    return new Intl.NumberFormat(this.locale).format(value);
  }

  /** 24 ore in Italia e in Germania, 12 in inglese americano: lo decide la lingua. */
  time(atMs: number): string {
    return this.timeFormat.format(new Date(atMs));
  }

  date(atMs: number): string {
    return this.dateFormat.format(new Date(atMs));
  }

  /**
   * «giovedì 25 ottobre» — e con la maiuscola iniziale, perché apre una frase.
   *
   * ## Solo la prima lettera
   *
   * Il mese **non** si mette in maiuscolo: in italiano, spagnolo e francese si scrive minuscolo, in
   * inglese e tedesco maiuscolo, e `Intl` lo sa già per ciascuna lingua. Forzarlo qui vorrebbe dire
   * sbagliare l'ortografia in tre lingue su sette per farne tornare una.
   *
   * La prima lettera invece si alza perché questa stringa comincia la riga: «giovedì 25 ottobre alle
   * 20:45» con la minuscola sembra un pezzo di frase tagliato.
   */
  weekdayDate(atMs: number): string {
    const formatted = this.weekdayDateFormat.format(new Date(atMs));
    // `charAt(0).toLocaleUpperCase(locale)`: in turco la `i` maiuscola è `İ`, e `toUpperCase()`
    // senza lingua darebbe `I`. Non è un dettaglio: «ikinci» è una parola turca che comincia così.
    return formatted.charAt(0).toLocaleUpperCase(this.locale) + formatted.slice(1);
  }

  /** «20:30 – 23:00» */
  timeRange(fromMs: number, toMs: number): string {
    return `${this.time(fromMs)} – ${this.time(toMs)}`;
  }
}
